export interface SamedayCounty {
  id: number;
  name: string;
}

export interface SamedayCity {
  id: number;
  name: string;
  county: number; // This is countyId in the database
  postalCode?: string;
}

const API_BASE = (import.meta as any).env?.VITE_API_URL
  ? ((import.meta as any).env.VITE_API_URL as string).replace(/\/$/, "")
  : "";

const ENDPOINTS = {
  NOMENCLATOR: {
    JUDETE: `${API_BASE}/api/geolocation/county`,
    LOCALITATI: `${API_BASE}/api/geolocation/city`,
    SEARCH: `${API_BASE}/api/geolocation/search`,
  },
};

class NomenclatorCache {
  private static instance: NomenclatorCache;
  private cache: Map<string, any> = new Map();
  private fetchPromises: Map<string, Promise<any>> = new Map();
  private lastUpdate: Map<string, number> = new Map();
  private readonly CACHE_DURATION = 6 * 60 * 60 * 1000; // 6 hours

  private constructor() {}

  static getInstance(): NomenclatorCache {
    if (!this.instance) {
      this.instance = new NomenclatorCache();
    }
    return this.instance;
  }

  private needsRefresh(key: string): boolean {
    const lastUpdate = this.lastUpdate.get(key);
    if (!lastUpdate) return true;
    return Date.now() - lastUpdate > this.CACHE_DURATION;
  }

  async getData<T>(key: string, fetchFn: () => Promise<T>): Promise<T> {
    // Return cached data if valid
    if (this.cache.has(key) && !this.needsRefresh(key)) {
      return this.cache.get(key);
    }

    // Return existing fetch promise if one is in progress
    const existingPromise = this.fetchPromises.get(key);
    if (existingPromise) {
      return existingPromise;
    }

    // Create new fetch promise
    try {
      const promise = fetchFn();
      this.fetchPromises.set(key, promise);

      const data = await promise;
      this.cache.set(key, data);
      this.lastUpdate.set(key, Date.now());
      return data;
    } finally {
      // Clean up the fetch promise regardless of success/failure
      this.fetchPromises.delete(key);
    }
  }

  clearCache() {
    this.cache.clear();
    this.lastUpdate.clear();
    this.fetchPromises.clear();
  }
}

export class NomenclatorService {
  private static instance: NomenclatorService;
  private cache = NomenclatorCache.getInstance();
  private readonly MAX_RETRY_ATTEMPTS = 2;
  private readonly BASE_RETRY_DELAY = 1000; // 1 second

  private constructor() {}

  static getInstance(): NomenclatorService {
    if (!this.instance) {
      this.instance = new NomenclatorService();
    }
    return this.instance;
  }

  private async fetchWithRetry<T>(
    url: string,
    options: RequestInit = {}
  ): Promise<T> {
    let attempt = 0;
    let lastError: Error | null = null;

    // Debug logging
    console.log(`🔍 Fetching from URL: ${url}`);
    console.log(`🌍 Base API URL: ${API_BASE || "(missing VITE_API_URL)"}/api`);
    console.log(`📋 All nomenclator endpoints:`, {
      JUDETE: ENDPOINTS.NOMENCLATOR.JUDETE,
      LOCALITATI: ENDPOINTS.NOMENCLATOR.LOCALITATI,
      SEARCH: ENDPOINTS.NOMENCLATOR.SEARCH,
    });

    if (!API_BASE) {
      throw new Error(
        "VITE_API_URL is not set. Please add VITE_API_URL to your .env (e.g., http://localhost:3001)"
      );
    }

    while (attempt < this.MAX_RETRY_ATTEMPTS) {
      try {
        const response = await fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
            Accept: "application/json",
            "Content-Type": "application/json",
            "ngrok-skip-browser-warning": "true", // Skip ngrok abuse interstitial
          },
        });

        console.log(
          `📊 Response status: ${response.status} ${response.statusText}`
        );
        console.log(`📄 Response URL: ${response.url}`);
        console.log(
          `🗂️ Response headers:`,
          Object.fromEntries(response.headers.entries())
        );

        if (!response.ok) {
          // Try to get the response text to see what we actually received
          const responseText = await response.text();
          console.error(
            `❌ HTTP ${response.status} response:`,
            responseText.slice(0, 500)
          );

          // Check if this looks like an ngrok error page
          if (
            responseText.includes("ngrok") &&
            responseText.includes("<!DOCTYPE")
          ) {
            throw new Error(`❌ Ngrok Error: The endpoint "${url}" doesn't exist on your backend server. 
            
🔧 Possible fixes:
1. Check if your backend has the route: GET /api/geolocation/county
2. Verify your backend server is running on port 5001
3. Test the endpoint directly: ${url}

Status: ${response.status} ${response.statusText}`);
          }

          throw new Error(
            `HTTP error! status: ${response.status} - ${responseText.slice(
              0,
              200
            )}`
          );
        }

        // Check if response is actually JSON
        const contentType = response.headers.get("content-type");
        if (!contentType || !contentType.includes("application/json")) {
          const responseText = await response.text();
          console.error(
            `🚫 Expected JSON but got ${contentType}:`,
            responseText.slice(0, 500)
          );

          // Check if this is an ngrok page
          if (
            responseText.includes("ngrok") &&
            responseText.includes("<!DOCTYPE")
          ) {
            throw new Error(`🚫 Backend Route Missing: The endpoint "${url}" returned an ngrok page instead of JSON data.

This means your backend doesn't have this route implemented.

🛠️ You need to implement: GET /api/geolocation/county and GET /api/geolocation/city in your backend.`);
          }

          throw new Error(
            `Invalid content type: ${contentType}. Expected JSON but got: ${responseText.slice(
              0,
              100
            )}...`
          );
        }

        const data = await response.json();
        console.log(`✅ Successfully parsed JSON:`, data);
        return data;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error("Unknown error");
        console.error(`❌ Attempt ${attempt + 1} failed:`, lastError.message);
        attempt++;

        if (attempt < this.MAX_RETRY_ATTEMPTS) {
          console.log(`🔄 Retrying in ${this.BASE_RETRY_DELAY * attempt}ms...`);
          await new Promise((resolve) =>
            setTimeout(resolve, this.BASE_RETRY_DELAY * attempt)
          );
        }
      }
    }

    throw lastError || new Error("Failed to fetch after retries");
  }

  async getCounties(): Promise<SamedayCounty[]> {
    return this.cache.getData<SamedayCounty[]>("counties", async () => {
      console.log("🏛️ Fetching counties...");
      const response = await this.fetchWithRetry<{ data: SamedayCounty[] }>(
        ENDPOINTS.NOMENCLATOR.JUDETE
      );
      return response.data;
    });
  }

  async getCitiesForCounty(countyId?: number): Promise<SamedayCity[]> {
    const cacheKey = countyId ? `cities_${countyId}` : "all_cities";
    return this.cache.getData<SamedayCity[]>(cacheKey, async () => {
      console.log("🏙️ Fetching cities...");
      const response = await this.fetchWithRetry<{ data: SamedayCity[] }>(
        ENDPOINTS.NOMENCLATOR.LOCALITATI
      );
      const allCities = response.data;

      // If countyId is provided, filter cities for that county
      if (countyId) {
        return allCities.filter((city) => city.county === countyId);
      }

      return allCities;
    });
  }

  async refreshCache(): Promise<void> {
    this.cache.clearCache();
    await this.getCounties();
  }
}

export const nomenclatorService = NomenclatorService.getInstance();
